import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { ArrowLeft, ChevronRight, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { FormSection } from '@/components/forms/Form';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormField, FormSelect, FormTextarea } from '@/components/forms/FormField';
import { FormCheckbox } from '@/components/forms/FormCheckbox';
import {
  useActivites,
  useDeleteActivite,
  useUpsertActivite,
  type UpsertActiviteInput,
} from '@/hooks/queries/useActivites';
import { useEntreprise } from '@/hooks/queries/useEntreprises';
import { useRole } from '@/hooks/useRole';
import { t } from '@/locales/i18n';
import type { ActiviteRow, CanalActivite } from '@/lib/database.types';

const schema = z.object({
  code: z.string().min(2, 'Min. 2 caractères'),
  nom: z.string().min(2, 'Min. 2 caractères'),
  canal: z.enum(['B2B', 'B2C', 'Mixte']),
  description: z.string().optional().nullable(),
  ordre: z.coerce.number().int().min(0).default(0),
  is_active: z.boolean(),
});

type ActiviteForm = z.infer<typeof schema>;

export function ActivitesPage(): JSX.Element {
  const { entrepriseId = '' } = useParams<{ entrepriseId: string }>();
  const { isAdmin } = useRole();
  const { data: entreprise } = useEntreprise(entrepriseId);
  const { data: activites = [], isLoading } = useActivites(entrepriseId);
  const upsert = useUpsertActivite();
  const remove = useDeleteActivite();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ActiviteRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ActiviteRow | null>(null);

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={`${t('parametres.organisation.activites.title')}${entreprise ? ' — ' + entreprise.nom : ''}`}
        subtitle="Liste des activités par canal (B2B, B2C, Mixte)."
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline">
              <Link to="/parametres/organisation">
                <ArrowLeft className="h-4 w-4" />
                {t('common.back')}
              </Link>
            </Button>
            {isAdmin && (
              <Button
                onClick={() => {
                  setEditing(null);
                  setDialogOpen(true);
                }}
              >
                <Plus className="h-4 w-4" />
                {t('parametres.organisation.activites.addActivite')}
              </Button>
            )}
          </div>
        }
      />

      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
          </div>
        ) : activites.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            Aucune activité.
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Code</th>
                  <th className="px-3 py-2 text-left">Nom</th>
                  <th className="px-3 py-2 text-left">Canal</th>
                  <th className="px-3 py-2 text-left">Ordre</th>
                  <th className="px-3 py-2 text-left">Statut</th>
                  <th className="px-3 py-2 text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {activites.map((a) => (
                  <tr key={a.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-3 py-2 font-mono text-xs">{a.code}</td>
                    <td className="px-3 py-2 font-medium">{a.nom}</td>
                    <td className="px-3 py-2">
                      <Badge
                        variant={
                          a.canal === 'B2B'
                            ? 'default'
                            : a.canal === 'B2C'
                              ? 'secondary'
                              : 'outline'
                        }
                      >
                        {a.canal}
                      </Badge>
                    </td>
                    <td className="num px-3 py-2 text-xs">{a.ordre}</td>
                    <td className="px-3 py-2">
                      {a.is_active ? (
                        <Badge variant="success">{t('common.active')}</Badge>
                      ) : (
                        <Badge variant="outline">{t('common.inactive')}</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button asChild variant="ghost" size="sm">
                          <Link
                            to={`/parametres/organisation/${entrepriseId}/activites/${a.id}/services`}
                          >
                            {t('parametres.organisation.activites.viewServices')}{' '}
                            <ChevronRight className="h-3 w-3" />
                          </Link>
                        </Button>
                        {isAdmin && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditing(a);
                                setDialogOpen(true);
                              }}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setConfirmDelete(a)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ActiviteFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSubmit={async (values) => {
          await upsert.mutateAsync({
            id: editing?.id,
            entreprise_id: entrepriseId,
            ...values,
          } as UpsertActiviteInput);
          toast.success(editing ? 'Activité mise à jour' : 'Activité créée');
          setDialogOpen(false);
        }}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title={`Supprimer l'activité « ${confirmDelete?.nom} » ?`}
        description="Tous les services associés seront aussi supprimés."
        loading={remove.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          try {
            await remove.mutateAsync(confirmDelete.id);
            toast.success('Activité supprimée');
            setConfirmDelete(null);
          } catch (err) {
            toast.error('Erreur : ' + (err as Error).message);
          }
        }}
      />
    </div>
  );
}

function ActiviteFormDialog({
  open,
  onOpenChange,
  editing,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: ActiviteRow | null;
  onSubmit: (values: ActiviteForm) => Promise<void>;
}): JSX.Element {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ActiviteForm>({
    resolver: zodResolver(schema),
    defaultValues: { code: '', nom: '', canal: 'B2B', ordre: 0, is_active: true },
  });

  useEffect(() => {
    if (open) {
      reset(
        editing
          ? {
              code: editing.code,
              nom: editing.nom,
              canal: editing.canal,
              description: editing.description,
              ordre: editing.ordre,
              is_active: editing.is_active,
            }
          : { code: '', nom: '', canal: 'B2B', ordre: 0, is_active: true },
      );
    }
  }, [open, editing, reset]);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={
        editing
          ? t('parametres.organisation.activites.editActivite')
          : t('parametres.organisation.activites.addActivite')
      }
      description="Code unique par entreprise (ex. ACAD-B2B-FORM)."
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit(async (v) => {
        await onSubmit(v);
      })}
    >
      <FormSection>
        <FormField label="Code" required {...register('code')} error={errors.code?.message} />
        <FormSelect
          label="Canal"
          required
          value={watch('canal')}
          onChange={(v) => setValue('canal', v as CanalActivite, { shouldValidate: true })}
          options={[
            { value: 'B2B', label: 'B2B' },
            { value: 'B2C', label: 'B2C' },
            { value: 'Mixte', label: 'Mixte' },
          ]}
        />
        <FormField
          label="Nom"
          required
          {...register('nom')}
          error={errors.nom?.message}
          fullWidth
        />
        <FormField type="number" label="Ordre d'affichage" {...register('ordre')} />
        <FormCheckbox label="Activité active" {...register('is_active')} />
        <FormTextarea label="Description (optionnelle)" {...register('description')} fullWidth />
      </FormSection>
    </FormDialog>
  );
}
